import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/solid'
import React, { Fragment, useState } from 'react'

export type Option = {
  key: string
  value: string
  enum?: any
}

export type SelectProps = {
  data: Option[]
  default?: Option
  label: string
  onChange: (value: Option) => void
  readonly?: boolean
  onNewClient?: () => void
  expFilter?: boolean
}

export default function Select(props: SelectProps) {
  const [selected, setSelected] = useState<Option>(
    props.data.find(option => option.key === props.default?.key) || { key: '', value: props.label },
  )

  const onChange = (value: Option) => {
    setSelected(value)
    props.onChange(value)
  }

  return (
    <div className='w-full border-b border-primary-400'>
      <Listbox value={selected} onChange={onChange} disabled={props.readonly}>
        <div className='relative'>
          <Listbox.Button
            name='select'
            className='relative w-full py-2 pl-2 pr-10 text-left bg-white rounded-lg cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-black focus-visible:ring-offset-2 sm:text-xs'
          >
            <label className={selected.key === '' ? 'mt-4' : ''} htmlFor='select'>
              {selected.key !== '' ? props.label : ''}
            </label>
            <span className={`block truncate font-light text-base ${selected.key === '' && 'text-primary-400'}`}>
              {selected.value}
            </span>
            <span className='absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none'>
              <ChevronDownIcon
                className='w-5 h-5 text-gray-400'
                aria-hidden='true'
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Listbox.Options className='absolute w-full py-1 mt-1 overflow-auto text-base bg-grey-400 rounded-lg shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50'>
              {props.data.map((option) => (
                <Listbox.Option
                  key={option.key}
                  className={({ active }) =>
                    `cursor-default select-none relative py-2 pl-10 pr-4 ${
                      active ? 'text-white bg-primary-400' : 'text-gray-900'
                    }`}
                  value={option}
                >
                  {({ selected }) => (
                    <Fragment>
                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                        {option.value}
                      </span>
                      {selected
                        ? (
                          <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-cta-200'>
                            <CheckIcon className='w-5 h-5' aria-hidden='true' />
                          </span>
                        )
                        : null}
                    </Fragment>
                  )}
                </Listbox.Option>
              ))}
              {props.onNewClient
                && (
                  <button className='w-full border-t border-primary-800' onClick={props.onNewClient}>
                    <Listbox.Option
                      key={'newClient'}
                      className={'cursor-pointer select-none relative py-2 pl-10 pr-4 text-white'}
                      value={'CreateNewClient'}
                    >
                      <span className={`block truncate text-left text-[#c04184] font-semibold`}>
                        Create New Client
                      </span>
                      <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-cta-200'>
                        <PlusIcon className='w-5 h-5' aria-hidden='true' />
                      </span>
                    </Listbox.Option>
                  </button>
                )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}
